import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  ArrowLeft, Target, Clock, Check, Zap, BarChart3, MapPin, Trophy,
} from 'lucide-react';
import {
  fetchActiveChallenge, fetchChallengeHistory,
  type ChallengeCard as ChallengeCardType,
} from '@/lib/api';
import NamedIcon from '@/components/ui/NamedIcon';
import styles from './ChallengesPage.module.css';

// Phase 2.1 slot mapping for the dynamic METRIC_ICON dispatch.
// Bolt for energy/sessions, barchart for kWh totals, mappin for
// location-based metrics. Falls back to 'bolt' to match the
// Lucide-side default for unknown metrics.
const METRIC_SLOT: Record<string, string> = {
  sessions: 'bolt',
  kwh: 'barchart',
  unique_stations: 'mappin',
};

interface Props { onNavigate: (path: string) => void }

const METRIC_ICON = {
  sessions: Zap,
  kwh: BarChart3,
  unique_stations: MapPin,
} as const;

function formatRemaining(endIso: string, t: TFunction<'challenges'>): string {
  const ms = new Date(endIso).getTime() - Date.now();
  if (ms <= 0) return t('remaining.ended');
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  if (days > 0) return t('remaining.daysHours', { days, hours });
  const mins = Math.floor((ms % 3_600_000) / 60_000);
  return t('remaining.hoursMins', { hours, mins });
}

function metricLabel(
  metric: ChallengeCardType['metric'],
  count: number,
  t: TFunction<'challenges'>,
): string {
  switch (metric) {
    case 'kwh': return 'kWh';
    case 'sessions': return t('metric.session', { count });
    case 'unique_stations': return t('metric.station', { count });
  }
}

/**
 * Weekly challenge hub — one hero card for the active challenge,
 * history rollup below. Rotation happens server-side on the Sunday
 * cron; this page just visualises the state.
 */
export function ChallengesPage({ onNavigate }: Props) {
  const { t } = useTranslation('challenges');
  const activeQ = useQuery({
    queryKey: ['challenges', 'active'],
    queryFn: fetchActiveChallenge,
    staleTime: 30_000,
  });
  const historyQ = useQuery({
    queryKey: ['challenges', 'history'],
    queryFn: () => fetchChallengeHistory(8),
    staleTime: 60_000,
  });

  if (activeQ.isLoading && !activeQ.data) {
    return <div className={styles.page}><p className={styles.loading}>{t('loading')}</p></div>;
  }

  return (
    <div className={styles.page}>
      <button className={styles.backFab} onClick={() => onNavigate('/profile')} aria-label={t('back')}>
        <ArrowLeft size={18} />
      </button>

      <header className={styles.header}>
        <div className={styles.headerIcon}>
          <NamedIcon name="target" fallback={<Target size={20} />} size={20} />
        </div>
        <div>
          <h1 className={styles.title}>{t('title')}</h1>
          <p className={styles.subtitle}>
            {t('subtitle')}
          </p>
        </div>
      </header>

      {activeQ.data && <ActiveCard card={activeQ.data.active} />}

      {historyQ.data && historyQ.data.length > 0 && (
        <section className={styles.history}>
          <h2 className={styles.sectionLabel}>{t('history')}</h2>
          {historyQ.data.map((c) => (
            <HistoryRow key={c.id} card={c} />
          ))}
        </section>
      )}
    </div>
  );
}

function ActiveCard({ card }: { card: ChallengeCardType }) {
  const { t } = useTranslation('challenges');
  const Icon = METRIC_ICON[card.metric] ?? Zap;
  const completed = card.status === 'completed';
  const pct = Math.min(100, Math.max(0, card.progressPct));

  return (
    <section className={`${styles.hero} ${completed ? styles.heroDone : ''}`}>
      <div className={styles.heroHead}>
        <div className={styles.heroIcon}>
          <NamedIcon
            name={METRIC_SLOT[card.metric] ?? 'bolt'}
            fallback={<Icon size={22} />}
            size={22}
          />
        </div>
        <div className={styles.heroTitles}>
          <p className={styles.heroName}>{card.name}</p>
          <p className={styles.heroDesc}>{card.description}</p>
        </div>
        <div className={styles.reward}>
          <span className={styles.rewardAmt}>+{card.rewardPoints}</span>
          <span className={styles.rewardLabel}>{t('pts')}</span>
        </div>
      </div>

      {/* Progress */}
      <div className={styles.progressBlock}>
        <div className={styles.progressRow}>
          <span className={styles.progressCurrent}>
            {card.metric === 'kwh'
              ? card.currentValue.toFixed(1)
              : Math.floor(card.currentValue)}
          </span>
          <span className={styles.progressOf}>
            / {card.threshold} {metricLabel(card.metric, card.threshold, t)}
          </span>
        </div>
        <div className={styles.progressBar}>
          <span
            className={styles.progressFill}
            style={{ width: `${Math.max(2, pct)}%` }}
            aria-hidden
          />
        </div>
      </div>

      <div className={styles.heroFoot}>
        {completed ? (
          <span className={styles.doneTag}>
            <Check size={13} /> {t('completed', { points: card.rewardPoints })}
          </span>
        ) : (
          <span className={styles.remaining}>
            <Clock size={12} /> {formatRemaining(card.weekEnd, t)}
          </span>
        )}
      </div>
    </section>
  );
}

function HistoryRow({ card }: { card: ChallengeCardType }) {
  const { t } = useTranslation('challenges');
  const Icon = METRIC_ICON[card.metric] ?? Zap;
  const completed = card.status === 'completed';
  return (
    <div className={`${styles.histRow} ${completed ? styles.histRowDone : ''}`}>
      <div className={styles.histIcon}><Icon size={14} /></div>
      <div className={styles.histText}>
        <p className={styles.histName}>{card.name}</p>
        <p className={styles.histSub}>
          {new Date(card.weekStart).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
          {' – '}
          {card.currentValue.toFixed(card.metric === 'kwh' ? 1 : 0)}
          /{card.threshold} {metricLabel(card.metric, card.threshold, t)}
        </p>
      </div>
      {completed ? (
        <span className={styles.histTagDone}>
          <Trophy size={11} /> +{card.rewardPoints}
        </span>
      ) : (
        <span className={styles.histTagMissed}>{t('missed')}</span>
      )}
    </div>
  );
}
